/**
 * DeepResearchModal.js - Multi-source deep research workflow
 * Uses the same modal shell and form styles as ProtestQueryModal / Dictionary workflows.
 */

import React, { useEffect, useState } from 'react';
import { Icons } from './Icons';

function DeepResearchModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  progress,
  initialValues,
  hasPdf,
  hasExcel,
  pdfData,
  excelData,
  pdfFilename,
  excelFilename,
}) {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('');
  const [depth, setDepth] = useState('standard');
  const [sources, setSources] = useState({
    web: true,
    communications: true,
    attachments: true,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialValues || {};
    setQuery(initial.query || '');
    setScope(initial.scope || '');
    setDepth(initial.depth || 'standard');
    setSources({
      web: initial.sources?.web !== false,
      communications: initial.sources?.communications !== false,
      attachments: initial.sources?.attachments !== false,
    });
    setError('');
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setError('Please enter a research question (at least 3 characters).');
      return;
    }
    setError('');
    onSubmit({
      query: trimmed,
      scope: scope.trim(),
      depth,
      sources,
      pdfData: sources.attachments ? pdfData : null,
      excelData: sources.attachments ? excelData : null,
      pdfFilename,
      excelFilename,
    });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const attachmentLabel = hasPdf
    ? pdfFilename || 'PDF attached'
    : hasExcel
      ? excelFilename || 'Excel attached'
      : null;
  const attachmentsAvailable = hasPdf || hasExcel;

  return (
    <div className="protest-modal-overlay" onClick={handleOverlayClick}>
      <div className="protest-modal deep-research-modal">
        <div className="protest-modal-header">
          <div className="protest-modal-title">
            <Icons.FileText />
            <h2>Deep Research</h2>
          </div>
          <button
            type="button"
            className="protest-modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <Icons.Close />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="protest-modal-content">
          <p className="deep-research-modal-desc">
            Run a multi-step research report across web sources, internal communications, and
            attached documents. Progress streams in chat while the job runs.
          </p>

          <div className="protest-location-section">
            <h3>Research question</h3>
            <div className="protest-location-field">
              <textarea
                id="deep-research-query"
                rows={4}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. Analyze NATO logistics trends and risks in 2026"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="protest-location-section">
            <h3>Scope / boundaries (optional)</h3>
            <div className="protest-location-field">
              <input
                type="text"
                id="deep-research-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                placeholder="Focus areas, exclusions, or time bounds"
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="protest-location-section">
            <h3>Data sources</h3>
            <div className="protest-source-options">
              <label className="protest-source-option">
                <input
                  type="checkbox"
                  checked={sources.web}
                  onChange={(e) => setSources((s) => ({ ...s, web: e.target.checked }))}
                  disabled={isLoading}
                />
                <span>Public web search</span>
              </label>
              <label className="protest-source-option">
                <input
                  type="checkbox"
                  checked={sources.communications}
                  onChange={(e) =>
                    setSources((s) => ({ ...s, communications: e.target.checked }))
                  }
                  disabled={isLoading}
                />
                <span>Internal communications</span>
              </label>
              <label
                className={`protest-source-option ${!attachmentsAvailable ? 'disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={sources.attachments}
                  onChange={(e) =>
                    setSources((s) => ({ ...s, attachments: e.target.checked }))
                  }
                  disabled={isLoading || !attachmentsAvailable}
                />
                <span>
                  Session attachments{' '}
                  {attachmentLabel ? `(${attachmentLabel})` : '(attach PDF or Excel in chat)'}
                </span>
              </label>
            </div>
          </div>

          <div className="protest-location-section">
            <h3>Depth</h3>
            <div className="protest-location-field">
              <select
                id="deep-research-depth"
                value={depth}
                onChange={(e) => setDepth(e.target.value)}
                disabled={isLoading}
              >
                <option value="standard">Standard</option>
                <option value="deep">Deep (more subtopics, longer run)</option>
              </select>
            </div>
          </div>

          {error && <div className="protest-error">{error}</div>}

          {isLoading && progress?.message && (
            <div className="deep-research-modal-progress">{progress.message}</div>
          )}

          <div className="protest-modal-actions">
            <button
              type="button"
              className="protest-btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className="protest-btn-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="protest-loading-spinner" />
                  Starting…
                </>
              ) : (
                <>
                  <Icons.Search />
                  Start research
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeepResearchModal;
